import * as React from 'react';
import { FC, useState } from 'react';

import { IconButton } from '@rbilabs/universal-components';
import { logButtonClick } from 'features/navigation/utils/analytics';

import ConfirmDialog from 'components/confirm-dialog';
import { withFavoriteActionGate } from 'components/favorite-action-gate';
import { FavoriteIcon } from 'components/favorite-icon';
import { SaveFavoriteOrder } from 'components/save-favorite-order';
import { toaster } from 'components/toaster/toaster';
import {
  GetUserFavoritesRefsDocument,
  ICartEntryInput,
  useCreateFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetUserFavoritesRefsQuery,
} from 'generated/graphql-gateway';
import { ClickEventComponentNames, CustomEventNames, EventTypes } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { cartEntriesHasAnOffer } from 'state/order/utils';
import { frontendToBackendCartEntryMap } from 'utils/cart';
import { mapBackendToFrontend } from 'utils/reorder';
import { routes } from 'utils/routing';

import { IFavoriteOrderButtonProps } from './types';

/**
 *
 * Favorite Order Button
 *
 */
const FavoriteOrderButtonContent: FC<React.PropsWithChildren<IFavoriteOrderButtonProps>> = ({
  order,
  iconProps,
}) => {
  const { rbiOrderId } = order;
  const [saveModalOpen, setSaveModalOpen] = useState<boolean>(false);
  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);
  const shouldDisplayHeart = !cartEntriesHasAnOffer(order.cart.cartEntries);

  const { data } = useGetUserFavoritesRefsQuery({
    skip: !shouldDisplayHeart,
  });

  const [deleteFavorite, { loading: deleteLoading }] = useDeleteFavoriteMutation({
    onCompleted: () => toaster.positive(t('removeFavoriteSucess')),
  });
  const [createFavorite, { loading: createLoading }] = useCreateFavoriteMutation({
    onCompleted: () => toaster.positive(t('saveFavoriteOrderSucess')),
  });

  const onClick = () => {
    if (isFavorite) {
      setRemoveModalOpen(true);
    } else {
      setSaveModalOpen(true);
    }
  };

  const entries = (order.cart.cartEntries.map(cartEntry =>
    frontendToBackendCartEntryMap(mapBackendToFrontend(cartEntry))
  ) as unknown) as ICartEntryInput[];
  const saveFavoriteOrder = async (name: string) => {
    return await createFavorite({
      variables: {
        input: {
          entries,
          name,
          ref: rbiOrderId,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [{ query: GetUserFavoritesRefsDocument }],
    });
  };

  const handleRemoveFavoriteOrder = () => {
    if (refFavorite?._id) {
      removeFavoriteOrder(refFavorite._id);
    }
    setRemoveModalOpen(false);
  };

  const removeFavoriteOrder = async (favoriteId: string) => {
    return await deleteFavorite({
      variables: {
        favoriteId,
      },
      awaitRefetchQueries: true,
      refetchQueries: [{ query: GetUserFavoritesRefsDocument }],
    });
  };

  const refFavorite = data?.userFavorites.favorites?.find(favorite => favorite?.ref === rbiOrderId);
  const isFavorite = !!refFavorite;

  const accessibilityLabelFavorites = isFavorite ? t('removeFavorite') : t('addFavorite');

  const loading = createLoading || deleteLoading;

  return shouldDisplayHeart ? (
    <>
      <IconButton
        variant="ghost"
        onPress={onClick}
        testID="favorite-order-icon"
        disabled={loading}
        accessibilityLabel={accessibilityLabelFavorites}
        icon={
          <FavoriteIcon
            iconName={iconProps?.name}
            isFavorite={isFavorite}
            disabled={loading}
            loading={loading}
          />
        }
      />

      {removeModalOpen && (
        <ConfirmDialog
          showDialog
          heading={t('removeFavorite')}
          body={t('removeFavoriteConfirm')}
          cancelLabel={t('cancel')}
          confirmLabel={t('remove')}
          onCancel={() => setRemoveModalOpen(false)}
          onConfirm={handleRemoveFavoriteOrder}
          modalAppearanceEventMessage="Delete Favorite Order"
        />
      )}
      {saveModalOpen && (
        <SaveFavoriteOrder
          showDialog={saveModalOpen}
          onSave={name => {
            saveFavoriteOrder(name.trim()).then(() => {
              logButtonClick({
                type: EventTypes.UserContent,
                attributes: {
                  'Source Page': `${routes.menu}/`,
                  Name: CustomEventNames.ADD_ITEM_TO_FAVORITES,
                  name: CustomEventNames.ADD_ITEM_TO_FAVORITES,
                  component: ClickEventComponentNames.BUTTON,
                },
              });
            });
            setSaveModalOpen(false);
          }}
          onDismiss={() => setSaveModalOpen(false)}
        />
      )}
    </>
  ) : null;
};

export const FavoriteOrderButton = withFavoriteActionGate(
  FavoriteOrderButtonContent,
  undefined,
  ({ order }) => !cartEntriesHasAnOffer(order.cart.cartEntries)
);
